import * as cmOnOffTypes from '../../consts/communicationLayer/cmOnOffTypes';
import * as autoStartModeTypes from '../../consts/iot/autoStartModeTypes';
import * as responsiveDrawModeTypes from '../../consts/iot/responsiveDrawModeTypes';
import * as yapResponsiveDrawModeTypes from '../../consts/yap/responsiveDrawModeTypes';
import * as yapDeviceEventChangeTypes from '../../consts/yap/yapDeviceEventChangeTypes';
import * as yapHolderStatusTypes from '../../consts/yap/yapHolderStatusTypes';
import * as yapUpdateSettingsTypes from '../../consts/yap/yapUpdateSettingsTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import {
    clearIotDeviceHolderData,
    setIsDeviceNotSupported,
    updateIotDeviceData,
} from '../../state/ducks/iotDevice/actions';
import {setHolderInfoReceived, setYapHolderConnected} from '../../state/ducks/yapEncrypted';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {
    makeSelectAssetByAssetId,
    makeSelectIsHolderByAssetId,
    makeSelectRequestActionTypeData,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import connectivityService from '../device/connectivityService';
import deviceAssetService from '../device/deviceAssetService';
import deviceTypesService from '../device/deviceTypesService';
import iccConsumerProducts from '../icc/iccConsumerProducts';
import iotDeviceService from '../iotDevice/iotDeviceService';
import {getMediaId} from '../mapping/yapMappings';
import marketConfigService from '../marketConfigService';
import productHelpers from '../product/productHelpers';
import {READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE_ON_SYSTEM_STATUS_CHANGED} from '../scp/scpConsts';
import * as dataCollectionService from '../uam/dataCollectionService';
import yapConnectionService from './yapConnectionService';

const updateDeviceCharacteristics = async (event) => {
    const details = event?.details;

    if (!details) return;

    const isBle = connectivityService.isBle();

    switch (event.eventType) {
        case yapDeviceEventChangeTypes.SYSTEM_STATUS:
            //for USB inside details itself
            let {holderState, holderDefect} = details;
            const {holderPlugged, holderConnection, characteristics} = details;
            const holderPluggedIn = holderPlugged || holderConnection === 'CONNECTED';

            if (holderState === undefined) {
                const {chargerSystemStatus} = characteristics || {};
                const {holderState: systemHolderState, holderDefect: systemHolderDefect} = chargerSystemStatus || {};

                //for BLE inside characteristics.chargerSystemStatus node
                holderState = systemHolderState;
                holderDefect = systemHolderDefect;
            }

            const isHolderConnected = holderPluggedIn && holderState !== yapHolderStatusTypes.UNPLUGGED;
            const {device} = makeSelectIotDeviceData()(getState()) || {};
            const {isHolderConnected: isHolderConnectedPrevState} = device || {};

            const onHolderDisconnected = () => {
                dispatch(clearIotDeviceHolderData());
                dispatch(setYapHolderConnected(false));
                dispatch(setHolderInfoReceived(false));
                dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
            };

            const updateHolderData = () => {
                const device = {isHolderConnected, holder_connected: holderPluggedIn, holder_defect: holderDefect};
                const holderData = {
                    holder_state: yapHolderStatusTypes.getIotHolderStatus(holderState),
                    holder_connected: holderPluggedIn,
                };

                dispatch(updateIotDeviceData({device, holder: holderData}));
            };

            if (!isHolderConnected) {
                onHolderDisconnected();
            } else if (!isHolderConnectedPrevState) {
                const {holderSerial, holderCodentify} = await yapConnectionService.initScpData({
                    isHolderOnly: true,
                    readHolderSerialNumberTimeoutMs: isBle
                        ? READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE_ON_SYSTEM_STATUS_CHANGED
                        : undefined,
                });

                if (!holderSerial && !holderCodentify) {
                    onHolderDisconnected();
                    return;
                }

                updateHolderData();
            } else {
                updateHolderData();
            }
            break;
        case yapDeviceEventChangeTypes.BATTERY_STATUS:
            const {batteryChargeUnified, chargerBatteryVoltage} = details;

            if (!batteryChargeUnified) return;

            const deviceData = isBle
                ? {battery_charge: batteryChargeUnified}
                : {battery_charge: batteryChargeUnified, batteryVoltage: chargerBatteryVoltage};

            dispatch(updateIotDeviceData({device: deviceData}));
            break;
        default:
            break;
    }
};

const updateDeviceSettings = ({type, newValue, isHolder = false}) => {
    const state = getState();
    const {newValue: actionData} = makeSelectRequestActionTypeData(isHolder)(state);
    const {newValue: actionValue, settingName} = actionData || {};
    const settingType = type ? type : settingName;
    const settingValue = type ? newValue : actionValue;

    if (settingType === undefined || settingValue === undefined) return;

    let data;

    switch (settingType) {
        case yapUpdateSettingsTypes.LED_INTENSITY:
            data = {ledData: {[isHolder ? 'holder' : 'device']: settingValue}};
            break;
        case yapUpdateSettingsTypes.VAPE_CLOUD_SIZE:
            const {heatingProfile} = settingValue;

            data = {vapeCloudSizeData: {vape_cloud_size: heatingProfile}};
            break;
        case yapUpdateSettingsTypes.SESSION_PROFILING:
            const {puffNumber, puffingTime} = settingValue;

            data = {sessionProfiling: {puff_number: puffNumber, puff_time: puffingTime}};
            break;
        case yapUpdateSettingsTypes.RESPONSIVE_DRAW_PROFILE:
            const {hapticPattern} = settingValue;
            let value = hapticPattern;

            if (hapticPattern === yapResponsiveDrawModeTypes.ALLEGRO) {
                value = responsiveDrawModeTypes.VIVACE;
            } else if (hapticPattern === yapResponsiveDrawModeTypes.VIVALDI) {
                value = responsiveDrawModeTypes.STACCATO;
            }

            data = {responsiveDrawData: {responsive_draw_profile: value}};
            break;
        case yapUpdateSettingsTypes.EXT_HAPTIC_PROFILE:
            const {durations} = settingValue;
            const extHapticProfileIsOn = durations ? durations[0]?.durationIsOn : undefined;

            data = {vibrationData: {extended_haptic_profile: extHapticProfileIsOn}};
            break;
        case yapUpdateSettingsTypes.BATTERY_CHARGED_VIBRATION:
            const {batteryCharged} = settingValue;

            data = {vibrationData: {battery_charged: batteryCharged}};
            break;
        case yapUpdateSettingsTypes.UI_BATTERY_CHARGED_VIBRATION:
            const {batteryChargedOneExp, batteryChargedTwoExp} = settingValue;
            const isOn = batteryChargedOneExp === cmOnOffTypes.ON && batteryChargedTwoExp === cmOnOffTypes.ON;

            data = {vibrationData: {start_charging: isOn}};
            break;
        case yapUpdateSettingsTypes.LIFT_UP_GESTURE:
            data = {gestureModeData: {holder: {status_gesture_pickup_enabled: getGestureState(settingValue)}}};
            break;
        case yapUpdateSettingsTypes.DOUBLE_TAP_GESTURE:
            data = {gestureModeData: {holder: {status_gesture_double_tap_enabled: getGestureState(settingValue)}}};
            break;
        case yapUpdateSettingsTypes.AUTO_START_HEATING:
            const {featureState} = settingValue;
            const autoStartValue =
                featureState === cmOnOffTypes.ON
                    ? autoStartModeTypes.AUTO_START_STATE_ENABLED
                    : autoStartModeTypes.AUTO_START_STATE_DISABLE;

            data = {autoStartData: {holder: {autostart_feature_state: autoStartValue}}};
            break;
        default:
            break;
    }

    if (data) {
        dispatch(updateIotDeviceData(data));
    }
};

const getGestureState = (settingValue) => {
    const {gestureState} = settingValue;

    return gestureState === cmOnOffTypes.ON;
};

const updateIotDevice = async (assetId, deviceInfo) => {
    const isGamBackend = backendService.isGamBackend();
    const deviceData = isGamBackend ? deviceInfo : deviceInfo?.device;
    const {deviceSpecific, firmwareVersion} = deviceData || {};
    const deviceSpecificData = isGamBackend ? deviceSpecific : deviceData;
    const {associatedDevices} = deviceData || {};
    const {
        responsiveDrawProfile,
        vapeCloudSize,
        parentProtection,
        ledIntensity,
        codentify,
        batteryInfo,
        extHapticProfile,
        heatingVibration,
        uiHeatingVibration,
        batteryChargedVibration,
        uiBatteryChargedVibration,
        systemStatus,
        liftUpGesture,
        doubleTapGesture,
        autoStartHeating,
        sessionProfiling,
        configurationCode,
    } = deviceSpecificData || {};

    await updateDeviceCharacteristics({
        eventType: yapDeviceEventChangeTypes.SYSTEM_STATUS,
        details: systemStatus,
    });

    const state = getState();
    const asset = makeSelectAssetByAssetId(assetId)(state);
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);
    const {batteryChargeUnified} = batteryInfo || {};
    const material = isGamBackend ? asset.material : deviceInfo?.material;
    const materialId = getMediaId(material);
    const singleMaterialId = getMediaId(material, true);
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({configurationCode, isHolder});

    updateMainDeviceData({
        version: firmwareVersion,
        batteryCharge: batteryChargeUnified,
        codentify: codentify?.codentify,
        isHolder,
        mediaId: materialId,
        materialColor: material?.materialColor,
        singleMediaId: singleMaterialId,
        engine: associatedDevices?.engine,
        isOriginalDuoDevice,
    });

    const isDeviceSupported = marketConfigService.isDeviceSupportedForceCheck(isHolder);

    if (!isDeviceSupported) {
        dispatch(setIsDeviceNotSupported());

        return false;
    }

    const updateSettings = (type, newValue) => {
        updateDeviceSettings({type, newValue, isHolder});
    };

    updateSettings(yapUpdateSettingsTypes.RESPONSIVE_DRAW_PROFILE, responsiveDrawProfile);
    updateSettings(yapUpdateSettingsTypes.VAPE_CLOUD_SIZE, vapeCloudSize);
    updateSettings(yapUpdateSettingsTypes.PARENT_PROTECTION, parentProtection);
    updateSettings(yapUpdateSettingsTypes.LED_INTENSITY, ledIntensity);
    updateSettings(yapUpdateSettingsTypes.HEATING_VIBRATION, heatingVibration);
    updateSettings(yapUpdateSettingsTypes.EXT_HAPTIC_PROFILE, extHapticProfile);
    updateSettings(yapUpdateSettingsTypes.BATTERY_CHARGED_VIBRATION, batteryChargedVibration);
    updateSettings(yapUpdateSettingsTypes.LIFT_UP_GESTURE, liftUpGesture);
    updateSettings(yapUpdateSettingsTypes.DOUBLE_TAP_GESTURE, doubleTapGesture);
    updateSettings(yapUpdateSettingsTypes.AUTO_START_HEATING, autoStartHeating);
    updateSettings(yapUpdateSettingsTypes.UI_BATTERY_CHARGED_VIBRATION, uiBatteryChargedVibration);
    updateSettings(yapUpdateSettingsTypes.UI_HEATING_VIBRATION, uiHeatingVibration);
    updateSettings(yapUpdateSettingsTypes.SESSION_PROFILING, sessionProfiling);

    iotDeviceService.checkIfDeviceIsInSblModeStatic();
    return true;
};

const updateMainDeviceData = ({
    version,
    batteryCharge,
    codentify,
    isHolder,
    mediaId,
    materialColor,
    singleMediaId,
    engine,
    isOriginalDuoDevice,
}) => {
    const iotDevice = makeSelectIotDeviceData()(getState());
    const {device, holder} = iotDevice || {};
    const curDevice = isHolder ? holder : device;
    const type = curDevice?.type;

    if (!device) return;

    const innerData = {
        version: version || curDevice?.version,
        battery_charge: batteryCharge || curDevice?.battery_charge,
    };

    const isCodentifyValid = productHelpers.isOriginalCodentifyValid(codentify);

    if (
        isOriginalDuoDevice &&
        (type === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER || type === DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER)
    ) {
        innerData.type = isHolder
            ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER
            : DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
        if (!isHolder) {
            dispatch(updateIotDeviceData({holder: {type: DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER}}));
        }
    } else if (
        isOriginalDuoDevice === false &&
        (type === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER ||
            type === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER)
    ) {
        innerData.type = isHolder ? DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER : DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER;
        if (!isHolder) {
            dispatch(updateIotDeviceData({holder: {type: DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER}}));
        }
    }

    if (isCodentifyValid) {
        innerData.codentify = codentify;
    }

    if (mediaId) {
        innerData.mediaId = mediaId;
        innerData.colorHex = productHelpers.getHexColorByMaterialColor(device.type, materialColor);

        if (isHolder) {
            innerData.singleHolderMediaId = singleMediaId;
        }
    }

    const data = {
        [isHolder ? 'holder' : 'device']: innerData,
    };

    loadDeviceAssets(data);
    dispatch(updateIotDeviceData(data));

    updateEngineData(isHolder, engine);
};

const updateEngineData = (isHolder, engine) => {
    if (engine) {
        const {isSBLMode} = engine;

        engine.is_device_in_sbl_mode = isSBLMode;
        const engineData = {
            [isHolder ? 'holder_engine' : 'device_engine']: engine,
        };

        dispatch(updateIotDeviceData(engineData));
    }
};

const loadDeviceAssets = (data) => {
    const {device, holder} = data || {};
    const mediaNamesList = deviceAssetService.getDeviceAssetsList([
        device?.mediaId,
        holder?.mediaId,
        holder?.singleHolderMediaId,
    ]);

    if (mediaNamesList.length) {
        iccConsumerProducts.getProductsAssets(mediaNamesList);
    }
};

const updateAssetMedia = (assets) => {
    if (!assets?.length) return;

    const asset = assets[0];
    const {material, configurationCode, isHolder} = asset || {};
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({
        configurationCode,
        material,
        isHolder,
    });

    const materialId = getMediaId(asset.material);
    const singleMaterialId = getMediaId(asset.material, true);

    updateMainDeviceData({
        isHolder,
        mediaId: materialId,
        materialColor: material?.materialColor,
        singleMediaId: singleMaterialId,
        isOriginalDuoDevice,
    });
};

export default {
    updateAssetMedia,
    updateDeviceCharacteristics,
    updateDeviceSettings,
    updateIotDevice,
    updateEngineData,
};
